import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { updateError } from './user';
import { dispatch } from '../store';
import { getPracticeId } from './clincheck';

const initialState = {
    loadingForWpTemplate: false,
    wpTemplateListGlobal: null,
    showWpSubjectListGlobal: false,
};

const slice = createSlice({
    name: 'whatsappMessages',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        setLoadingForWpTemplate(state, action) {
            state.loadingForWpTemplate = action.payload;
        },
        setWpTemplateListGlobal(state, action) {
            state.wpTemplateListGlobal = action.payload;
        },
        updateShowWpSubjectList(state, action) {
            state.showWpSubjectListGlobal = action.payload;
        },
        updateTemplateUsageType(state, action) {
            const { templateId, usageType } = action.payload;
            if (Array.isArray(state.wpTemplateListGlobal)) {
                state.wpTemplateListGlobal = state.wpTemplateListGlobal.map((t) =>
                    t.id === templateId ? { ...t, usage_type: usageType } : t
                );
            }
        },
        removeTemplateById(state, action) {
            if (Array.isArray(state.wpTemplateListGlobal)) {
                state.wpTemplateListGlobal = state.wpTemplateListGlobal.filter(t => t.id !== action.payload);
            }
        },
        bulkUpdateUsageType(state, action) {
            const { ids, usageType } = action.payload;
            if (Array.isArray(state.wpTemplateListGlobal)) {
                state.wpTemplateListGlobal = state.wpTemplateListGlobal.map(t =>
                    ids.includes(t.id) ? { ...t, usage_type: usageType } : t
                );
            }
        },
        revertUsageTypes(state, action) {
            const oldValues = action.payload; // { [id]: usageType }
            if (Array.isArray(state.wpTemplateListGlobal)) {
                state.wpTemplateListGlobal = state.wpTemplateListGlobal.map(t =>
                    oldValues[t.id] !== undefined ? { ...t, usage_type: oldValues[t.id] } : t
                );
            }
        },
        updateSingleUsageType(state, action) {
            const { id, usageType } = action.payload;
            if (Array.isArray(state.wpTemplateListGlobal)) {
                const idx = state.wpTemplateListGlobal.findIndex(t => t.id === id);
                if (idx !== -1) state.wpTemplateListGlobal[idx] = { ...state.wpTemplateListGlobal[idx], usage_type: usageType };
            }
        },
    }
});

export default slice.reducer;

export const { reset, updateShowWpSubjectList, setWpTemplateListGlobal, updateTemplateUsageType, setLoadingForWpTemplate, removeTemplateById, bulkUpdateUsageType, revertUsageTypes, updateSingleUsageType } = slice.actions;

export const handleLoading = (state) => {
    if (state === false) {
        setTimeout(() => {
            dispatch(slice.actions.setLoadingForWpTemplate(state));
        }, 500);
    } else {
        dispatch(slice.actions.setLoadingForWpTemplate(state));
    }
};

const getQuery = (data) => {
    const params = [];
    if (data.search) {
        params.push(`query=${data.search}`);
    }
    if (data.usageTypeFilter?.length > 0) {
        params.push(`usage_type__in=${data.usageTypeFilter.join(',')}`);
    }
    return params.length > 0 ? `?${params.join('&')}` : '';
};

export function getSearchWpTemplateList(data) {
    const { wpAccountId } = data;
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        // Guard: skip API call if business account PK is missing
        if (!wpAccountId) return;
        dispatch(slice.actions.updateShowWpSubjectList(true));
        try {
            handleLoading(true);
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${getQuery(data)}`);
            if (response.status === 200) {
                dispatch(slice.actions.setWpTemplateListGlobal(response.data.results));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
            handleLoading(false);
        } catch (error) {
            dispatch(updateError(error));
            handleLoading(false);
        }
    };
}

export function updateWpContextMap(payload) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        try {
            const { wpAccountId, context, id } = payload ?? {};
            // Guard: skip API call if business account PK or template id is missing
            if (!wpAccountId || !id) return;
            const practiceId = getPracticeId();
            const reqBody = {
                template_context_mapping: context
            };
            const response = await callApi(`/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${id}/`, 'PATCH', reqBody);
            if (response.status === 200) {
                dispatch(getSearchWpTemplateList({ wpAccountId }));
            }
        } catch (err) {
            console.error(err);
        }
    };
}

export function get10WpTemplateList({ wpAccountId, usageTypeFilter }) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        // Guard: skip API call if business account PK is missing
        if (!wpAccountId) return;
        const practiceId = getPracticeId();
        dispatch(slice.actions.updateShowWpSubjectList(true));
        try {
            handleLoading(true);
            const query = getQuery({ usageTypeFilter });
            const response = await callApi(`/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${query}`);
            handleLoading(false);
            if (response.status === 200) {
                dispatch(slice.actions.setWpTemplateListGlobal(response.data.results));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError(error));
            handleLoading(false);
        }
    };
}